import { cashierAxios } from "../../axiosInstance";
import { isNetworkError, newRef, readJson, writeJson } from "./storage";
import { clearOfflineCart, getOfflineCart, isLocalId } from "./offlineCart";

// Sales taken without a connection wait here until they can be replayed to
// orders/store. Each carries a client_ref so the server can ignore a replay it
// has already processed.

const QUEUE_KEY = "sales";
const EVENT = "01pos:sales-queue";

export const getQueue = () => readJson(QUEUE_KEY, []);

const setQueue = (queue) => {
  writeJson(QUEUE_KEY, queue);
  window.dispatchEvent(new Event(EVENT));
};

export const subscribeQueue = (fn) => {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
};

export const enqueueSale = (payload) => {
  const ref = newRef();
  const sale = {
    ref,
    payload: { ...payload, client_ref: ref, offline: true },
    createdAt: new Date().toISOString(),
    status: "pending",
    error: null,
  };
  setQueue([...getQueue(), sale]);
  return sale;
};

// Short readable number for the offline receipt, e.g. OFF-3F9A2C.
export const offlineOrderNo = (sale) =>
  `OFF-${sale.ref.replace(/-/g, "").slice(0, 6).toUpperCase()}`;

export const retryFailed = () =>
  setQueue(getQueue().map((s) => (s.status === "failed" ? { ...s, status: "pending", error: null } : s)));

export const discardSale = (ref) => setQueue(getQueue().filter((s) => s.ref !== ref));

let inFlight = null;

// Replays pending and previously failed sales oldest first, so nobody has to
// press retry. Returns {synced, failed, newlyFailed, remaining, stopped}.
// Stops at the first connection problem or expired login.
export const syncSales = () => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const result = { synced: 0, failed: 0, newlyFailed: 0, remaining: 0, stopped: null };
    if (navigator.onLine === false) {
      result.remaining = getQueue().filter((s) => s.status === "pending").length;
      result.stopped = "offline";
      return result;
    }
    for (const sale of getQueue().filter((s) => s.status === "pending" || s.status === "failed")) {
      // An empty order can never be accepted; drop it rather than strand it.
      if (!sale.payload?.carts?.length) {
        setQueue(getQueue().filter((s) => s.ref !== sale.ref));
        continue;
      }
      try {
        await cashierAxios.post("orders/store", sale.payload);
        setQueue(getQueue().filter((s) => s.ref !== sale.ref));
        result.synced += 1;
      } catch (error) {
        const status = error?.response?.status;
        if (isNetworkError(error)) {
          result.stopped = "offline";
          break;
        }
        if (status === 401) {
          result.stopped = "auth";
          break;
        }
        if (status >= 500 || !status) {
          result.stopped = "server";
          break;
        }
        const message = error?.response?.data?.message || error.message;
        setQueue(
          getQueue().map((s) =>
            s.ref === sale.ref ? { ...s, status: "failed", error: message } : s
          )
        );
        result.failed += 1;
        if (sale.status === "pending") result.newlyFailed += 1;
      }
    }
    result.remaining = getQueue().filter((s) => s.status === "pending").length;
    return result;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
};

// Pushes an offline cart that was never checked out to the server once it is
// reachable again. Sales go first so their checkout cannot swallow these items.
export const flushOfflineCart = async (customerId) => {
  const cart = getOfflineCart(customerId);
  if (!cart) return;
  const sales = await syncSales();
  if (sales.stopped || sales.remaining > 0) throw new Error("Sales not synced yet");

  for (const id of cart.deleted) {
    try {
      await cashierAxios.delete(`carts/delete/${id}`);
    } catch (error) {
      if (isNetworkError(error)) throw error;
    }
  }
  for (const item of cart.items) {
    if (isLocalId(item.id)) {
      await cashierAxios.post("carts/store", {
        customer_id: customerId,
        product_id: item.product_id,
        price: item.price,
        quantity: item.quantity,
        title: "cart add",
      });
    } else if (item.dirty) {
      await cashierAxios.put(`carts/update/${item.id}`, {
        product_id: item.id,
        quantity: item.quantity,
        customer_id: customerId,
      });
    }
  }
  clearOfflineCart(customerId);
};
